import './ReportFormSectionConfig.scss';
import { FieldPath } from '@form-ts/core';
import { pipe } from 'fp-ts/function';
import * as Eq from 'io-ts/Eq';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { HorizontalDivider } from 'src/modules/common/components/HorizontalDivider';
import { FILE_MAX_SIZE_FILED_MB } from 'src/modules/common/constants/fileField';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { HelperText01 } from 'src/modules/common/typography/HelperText01';
import { CheckboxField } from 'src/modules/form/components/CheckboxField';
import { FormError } from 'src/modules/form/types/FormError';
import { ReportAttachment } from 'src/modules/report/types/ReportAttachment';
import { ReportFormData } from 'src/modules/report/types/ReportFormData';
import { ReportFormFieldAttachment } from 'src/modules/report/views/ReportFormFieldAttachment';
import { ReportFormFieldUpload } from 'src/modules/report/views/ReportFormFieldUpload';
import { isNotNull } from 'src/utils/guard';

type Props = {
  readonly field: FieldPath<ReportFormData, FormError, ReportFormData['appendix']>;
  readonly attachments: ReadonlyArray<ReportAttachment>;
  readonly onUpload: (file: File) => void;
  readonly onDelete: (attachment: ReportAttachment) => void;
};

export const ReportFormSectionConfigAppendix = ({
  field,
  attachments,
  onUpload,
  onDelete,
}: Props): React.ReactElement => {
  useEffect(() => {
    const stored = pipe(
      field.form.currentState,
      field.at('files').value.get,
    );
    const visible = attachments
      .map((it) => (it.state === 'success' ? it.file : null))
      .filter(isNotNull);

    if (!EQ_ASSET_LIST.equals(stored, visible)) {
      field.form.change(pipe(
        field.form.currentState,
        field.at('files').value.set(visible),
      ));
    }
  }, [field, attachments]);

  return (
    <div className="bp-report-form-section-config">
      <div className="bp-report-form-section-config__header">
        <BodyMedium01>
          <FormattedMessage id="report/form/appendix/header"/>
        </BodyMedium01>

        <CheckboxField field={field.at('enabled')}/>
      </div>

      <HorizontalDivider/>

      <div className="bp-report-form-section-config__content">
        <HelperText01 className="bp-report-form-section-config__appendix-title">
          <FormattedMessage id="report/form/appendix/files/title"/>
        </HelperText01>

        <div className="bp-report-form-section-config__hint">
          <FormattedMessage
            id="report/form/appendix/upload/hint"
            values={{ maxSize: FILE_MAX_SIZE_FILED_MB }}
          />
        </div>

        {attachments.map((attachment) => (
          <ReportFormFieldAttachment
            key={attachment.id}
            attachment={attachment}
            onDelete={onDelete}
          />
        ))}

        <ReportFormFieldUpload
          onUpload={onUpload}
        />
      </div>
    </div>
  );
};

const EQ_ASSET_FILE = Eq.struct({ id: Eq.string });
const EQ_ASSET_LIST = Eq.readonly(Eq.array(EQ_ASSET_FILE));
